import React, { ChangeEvent, DragEvent, useState } from "react";
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaSort } from "react-icons/fa";
import lodash from "lodash";

import { Select } from "./Select";
import { Spin } from "./Spin";
import { Pagination } from "./Pagination";
import { twMerge } from "tailwind-merge";
import { TableHeaderType } from "../interfaces";

interface ITable {
  id?: string;
  tableId?: string;
  headers: TableHeaderType<any>[];
  data: any[];
  sortColumn?: string;
  orderby?: string;
  onRowClick?: Function;
  onHeadingClick?: Function;
  skip?: number;
  take?: number;
  total?: number;
  onLeft?: Function;
  onRight?: Function;
  className?: string;
  onPageSizeChange?: (pageSize: number) => void;
  loading?: boolean;
  onChangePage?: (pagination: { page: number; pageSize: number }) => void;
  stripe?: boolean;
  pageSizeOption?: number[];
  headerHidden?: boolean;
  paginationHidden?: boolean;
  draggable?: boolean;
  rowClassName?: string;
  onDragStart?: (event: DragEvent<HTMLTableRowElement>, e: any) => void;
  onDragEnd?: (event: DragEvent<HTMLTableRowElement>, e: any) => void;
  onDrop?: (fromIndex: number, toIndex: number) => void;
}

export function TablePlus({
  id,
  tableId,
  data,
  headers,
  sortColumn,
  orderby,
  onRowClick,
  onHeadingClick,
  skip,
  take,
  total,
  onLeft,
  onRight,
  className,
  onPageSizeChange,
  loading = false,
  onChangePage,
  stripe = true,
  pageSizeOption,
  headerHidden = false,
  paginationHidden = false,
  draggable,
  onDragStart = () => {},
  onDragEnd = () => {},
  onDrop = () => {},
  rowClassName,
  ...props
}: ITable) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  return (
    <div
      {...props}
      id={id}
      className={twMerge("flex flex-col w-full max-w-full overflow-hidden shadow-xl bg-white ", className)}
    >
      <div className="overflow-x-auto  ">
        <div className="align-middle inline-block w-full">
          <div className=" border-b border-gray-200 sm:rounded-lg w-full">
            <Spin spinning={loading}>
              <table className="w-full divide-y divide-gray-200 table-auto border border-gray-100" id={tableId}>
                <thead className={`bg-gray-50  ${headerHidden ? "hidden" : ""}`}>
                  <tr>
                    {headers.map(({ sort = true, ...e }, i) => (
                      <th
                        key={i}
                        scope="col"
                        className={
                          "px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider " +
                          (typeof e === "string" ? "" : sort && onHeadingClick ? "cursor-pointer" : "")
                        }
                        onClick={() => {
                          if (sort && onHeadingClick) onHeadingClick(e);
                        }}
                        style={{ width: e.width }}
                      >
                        {typeof e === "string" ? (
                          e
                        ) : (
                          <span className="flex items-center">
                            <p>{e.name}</p>
                            {sort && onHeadingClick ? (
                              sortColumn === e.value && orderby ? (
                                orderby === "ASC" ? (
                                  <FaChevronDown className="ml-2 shrink-0" />
                                ) : (
                                  <FaChevronUp className="ml-2 shrink-0" />
                                )
                              ) : (
                                <FaSort className="ml-2 shrink-0 text-gray-300" />
                              )
                            ) : sort && sortColumn === e.value && orderby ? (
                              orderby === "ASC" ? (
                                <FaChevronDown className="ml-2" />
                              ) : orderby === "DESC" ? (
                                <FaChevronUp className="ml-2" />
                              ) : (
                                ""
                              )
                            ) : (
                              ""
                            )}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td className="p-10 text-center text-gray-400" colSpan={100}>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    data.map((e, i) => (
                      <tr
                        key={e.id || i}
                        className={twMerge(
                          "group transition-all duration-75",
                          stripe ? (i % 2 !== 0 ? "bg-white " : "bg-gray-200 ") : "border-b ",
                          onRowClick ? "cursor-pointer" : "",
                          rowClassName,
                          draggedIndex === i ? "bg-blue-100 opacity-70" : "",
                          dragOverIndex === i && draggedIndex !== null ? "border-2 border-blue-500" : ""
                        )}
                        draggable={draggable}
                        onDragStart={(event) => {
                          setDraggedIndex(i);
                          onDragStart(event, e);
                          // console.log("onDragStart", i);
                        }}
                        onDragEnd={(event) => {
                          setDraggedIndex(null);
                          setDragOverIndex(null);
                          onDragEnd(event, e);
                          // console.log("onDragEnd", i);
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragOverIndex(i);
                          // console.log("onDragOver", i);
                        }}
                        onDrop={() => {
                          if (draggedIndex !== null) onDrop(draggedIndex, i);
                        }}
                      >
                        {headers.map((f, headerIndex) => {
                          let render = "";
                          if (f.render) {
                            render = f.render(e, i, f.value);
                          } else if (f.value) {
                            const val = lodash.get(e, f.value);
                            render = typeof val === "string" ? val : val !== null ? JSON.stringify(val) : "";
                          }
                          return (
                            <td
                              key={"header_" + i + "_" + headerIndex}
                              className={twMerge("px-6 py-4 text-sm font-medium text-gray-900 ", f.className)}
                              onClick={(event) => {
                                if (event.view.document.getSelection()?.type === "Range") return;
                                if (f.disableRowClick) return;
                                if (onRowClick) onRowClick(e);
                              }}
                            >
                              {render || f.default}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Spin>
          </div>
        </div>
      </div>
      {skip !== undefined && total !== undefined && (
        <div className={`pagination mt-5 ${paginationHidden ? "hidden" : ""}`}>
          <div className="flex justify-between px-2 py-2">
            {onPageSizeChange ? (
              <Select
                id="limit"
                label="Number of Rows"
                options={
                  pageSizeOption
                    ? [
                        { name: "All", value: "" },
                        ...pageSizeOption.map((s) => ({ name: s.toString(), value: s.toString() })),
                      ]
                    : [
                        { name: "All", value: "9999999" },
                        { name: "10", value: "10" },
                        { name: "20", value: "20" },
                        { name: "50", value: "50" },
                        { name: "100", value: "100" },
                      ]
                }
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (onPageSizeChange) {
                    onPageSizeChange(parseInt(e.target.value));
                  }
                }}
                value={`${take}`}
                className="flex flex-row items-center gap-3"
              />
            ) : (
              <span></span>
            )}
            {take !== undefined ? (
              onChangePage ? (
                <div className="flex items-center justify-center">
                  <Pagination
                    total={total}
                    page={Math.floor(skip / take) + 1}
                    pageSize={take}
                    onChangePage={onChangePage}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <FaChevronLeft className="text-md mr-2 cursor-pointer" onClick={() => onLeft?.()} />
                  <p className="text-lg">
                    {skip + 1} - {Math.min(total, skip + take)} of {total}
                  </p>
                  <FaChevronRight className="text-md ml-2 cursor-pointer" onClick={() => onRight?.()} />
                </div>
              )
            ) : (
              <span></span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
